<script>
import { GlAlert, GlLink, GlSprintf } from '@gitlab/ui';
import * as Sentry from '@sentry/browser';
import { mapSystemToFriendlyError } from '~/javascripts/lib/error_utils';
import { BASE_ERROR_DICTIONARY } from '../lib/error_dictionary';

export default {
  name: 'ErrorAlert',
  components: {
    GlAlert,
    GlLink,
    GlSprintf,
  },
  props: {
    error: {
      type: [Error, Object, String],
      required: true,
    },
    alertVariant: {
      type: String,
      required: false,
      default: 'danger',
    },
    errorDictionary: {
      type: Object,
      required: false,
      default: () => BASE_ERROR_DICTIONARY,
    },
    defaultError: {
      type: Object,
      required: false,
      default: undefined,
    },
    additionalLinks: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    dismissible: {
      type: Boolean,
      required: false,
      default: false,
    },
    placeholders: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  computed: {
    friendlyError() {
      return mapSystemToFriendlyError(this.error, this.errorDictionary, this.defaultError);
    },
    friendlyErrorLinks() {
      return { ...this.friendlyError.links, ...this.additionalLinks };
    },
    errorLinkKeys() {
      // Iterating in v-for over objects not supported until Vue3
      // https://github.com/vuejs/vue/issues/6644
      return Object.keys(this.friendlyErrorLinks);
    },
    placeholderKeys() {
      return Object.keys(this.placeholders);
    },
    slotKeys() {
      return [...this.errorLinkKeys, ...this.placeholderKeys];
    },
  },
  created() {
    this.validatePlaceholders();
  },
  methods: {
    validatePlaceholders() {
      const { message } = this.friendlyError;
      const placeholderPattern = /%{(\w+?)(?:Start|End)?}/g;
      const matches = message.matchAll(placeholderPattern);

      const requiredKeys = new Set(Array.from(matches, (match) => match[1]));
      const missingPlaceholders = [...requiredKeys].filter((key) => !this.slotKeys.includes(key));

      if (missingPlaceholders.length > 0) {
        const error = new Error(
          `Missing placeholder values for keys: ${missingPlaceholders.join(', ')}`,
        );
        Sentry.captureException(error, { tags: { vue_component: this.$options.name } });
      }
    },
  },
};
</script>

<template>
  <gl-alert
    :dismissible="dismissible"
    class="gl-my-5"
    :variant="alertVariant"
    :title="friendlyError.title"
    @dismiss="$emit('dismiss')"
  >
    <gl-sprintf :message="friendlyError.message">
      <template v-for="key in slotKeys" #[key]="{ content }">
        <!-- For link slots -->
        <gl-link
          v-if="errorLinkKeys.includes(key)"
          :key="key"
          class="!gl-no-underline"
          :href="friendlyErrorLinks[key]"
          target="_blank"
          >{{ content }}</gl-link
        >
        <!-- For placeholder slots -->
        <span v-else-if="placeholderKeys.includes(key)" :key="key">{{ placeholders[key] }}</span>
      </template>
    </gl-sprintf>
  </gl-alert>
</template>
